import { useState } from "react";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { Loader2, Save, Shield, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next1) {
      toast.error("Fill all fields");
      return;
    }
    if (next1 !== next2) {
      toast.error("New passwords do not match");
      return;
    }
    if (next1.length < 4) {
      toast.error("New password must be at least 4 characters");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/password", {
        current_password: current,
        new_password: next1,
      });
      // Update stored token to the new password so subsequent requests keep working
      setToken(data.token);
      toast.success("Password changed. New sign-ins must use the new password.");
      setCurrent("");
      setNext1("");
      setNext2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-2xl">
      <div className="mb-8 animate-in-up">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
          Access
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
          Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Rotate the admin password used to sign in to this console.
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 animate-in-up">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="text-base font-bold">Change admin password</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Stored in MongoDB — overrides the <span className="mono">ADMIN_PASSWORD</span> in
              backend/.env.
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Current password
            </Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="settings-current-input"
              className="mt-1.5 h-11"
              autoComplete="current-password"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              New password
            </Label>
            <Input
              type="password"
              value={next1}
              onChange={(e) => setNext1(e.target.value)}
              data-testid="settings-new-input"
              className="mt-1.5 h-11"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Confirm new password
            </Label>
            <Input
              type="password"
              value={next2}
              onChange={(e) => setNext2(e.target.value)}
              data-testid="settings-confirm-input"
              className="mt-1.5 h-11"
              autoComplete="new-password"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            data-testid="settings-save-btn"
            className="h-11"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Change password
          </Button>
        </form>

        <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground border-t border-border pt-4">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Your current session token will be rotated automatically. Other logged-in
            devices will need to sign in again with the new password.
          </span>
        </div>
      </div>
    </div>
  );
}
